/**
 * Global Axios interceptor ສຳລັບ customer website
 * ====================================================
 * ★ ຈັດການ 401 (token ໝົດອາຍຸ) ອັດຕະໂນມັດ
 * ★ Silent Token Refresh — ໃຊ້ refreshToken ຂໍ accessToken ໃໝ່
 * ★ ຈັດການ network error (server ບໍ່ຕອບ)
 * ★ ປ້ອງກັນ redirect ຊ້ຳ (ໃຊ້ flag)
 * ★ Queue pattern — ກັນ refresh ຊ້ຳ ເມື່ອ 401 ມາຫຼາຍ calls ພ້ອมກັນ
 */
import axios from "axios";
import Swal from "sweetalert2";
// ★ นำเข้าเฉพาะโมดูลที่ใช้จริง (เดิม import ทั้ง crypto-js = โหลดทุก algorithm)
import AES from "crypto-js/aes";
import Utf8 from "crypto-js/enc-utf8";
import { SERVER_URL } from "../api";
import { encryptData } from "../utils/encrypt";

const _RKEY = process.env.REACT_APP_ENCRYPTION_KEY;
// ★ Zero-downtime rotation: ລອງ key ໃໝ່ກ່ອນ, fallback key ເກົ່າ (LEGACY)
const _RKEYS = [process.env.REACT_APP_ENCRYPTION_KEY, process.env.REACT_APP_ENCRYPTION_KEY_LEGACY].filter(Boolean);

function _decryptPayload(raw) {
  try {
    if (!raw || typeof raw !== "object" || typeof raw.d !== "string") return raw;
    // ★ ລອງທຸກ key ຈົນກວ່າຈະ decrypt ສຳເລັດ (ຮອງຮັບ rotation)
    for (const _k of _RKEYS) {
      try {
        const bytes = AES.decrypt(raw.d, _k);
        const text = bytes.toString(Utf8);
        if (text) return JSON.parse(text);
      } catch (_e) { /* ລອງ key ຕໍ່ໄປ */ }
    }
    return raw;
  } catch {
    return raw;
  }
}

let isRedirecting = false;
let isRefreshing = false;
let refreshQueue = []; // ★ Queue: ເກັບ requests ທີ່ລໍຖ້າ refresh ແລ້ວ

// ★ ປ່ອຍ queue ທັງໝົດ — retry ຫຼື reject
function processQueue(error, newToken) {
  refreshQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(newToken);
    }
  });
  refreshQueue = [];
}

// ★ Redirect ໄປ login — ລ້າງ token ທັງໝົດ
function forceLogout() {
  if (isRedirecting) return;
  isRedirecting = true;
  localStorage.removeItem("USER_WEB");
  localStorage.removeItem("ACCESS_TOKEN_WEB");
  localStorage.removeItem("REFRESH_TOKEN_WEB");
  Swal.fire({
    icon: "warning",
    title: "Session ໝົດອາຍຸ",
    text: "ກະລຸນາເຂົ້າສູ່ລະບົບໃໝ່",
    confirmButtonText: "ເຂົ້າສູ່ລະບົບ",
    confirmButtonColor: "#f5a623",
    allowOutsideClick: false,
  }).then(() => {
    isRedirecting = false;
    window.location.href = "/login";
  });
}

export function setupAxiosInterceptors() {
  // ============================================================
  // 📤 REQUEST INTERCEPTOR — แนบ Authorization + x-encrypted อัตโนมัติ
  // ============================================================
  // ก่อนหน้านี้ ทุกไฟล์ต้องเพิ่ม headers เอง → มีบาง endpoint ลืม (เช่น Profile.js)
  // ตอนนี้ใส่กลางที่เดียว → ทุก request ปลอดภัยเท่ากัน
  //
  // กฎ:
  //   1. แนบ Authorization เฉพาะเมื่อมี token อยู่ (login แล้ว)
  //   2. ไม่แนบ x-encrypted ซ้ำถ้า component ส่งมาเอง (รองรับ backward-compat)
  //   3. ไม่แตะ headers ที่เป็น auth endpoints (/login, /register, /forget-password, /refresh-token)
  //   4. รองรับเฉพาะ request ไป SERVER_URL (ปล่อยผ่าน request อื่น เช่น ไป Google OAuth)
  axios.interceptors.request.use(
    (config) => {
      const url = config?.url || "";

      // ข้าม endpoints ที่ไม่ต้องการ auth/encryption
      const isAuthEndpoint =
        url.includes("/login") ||
        url.includes("/register") ||
        url.includes("/forget-password") ||
        url.includes("/refresh-token");

      if (!isAuthEndpoint) {
        // —— Authorization —— (ถ้ามี token และยังไม่ได้ตั้งเอง)
        if (!config.headers?.Authorization) {
          try {
            const raw = localStorage.getItem("ACCESS_TOKEN_WEB");
            if (raw && raw !== "undefined" && raw !== "null") {
              const token = JSON.parse(raw);
              if (token) config.headers.Authorization = `Bearer ${token}`;
            }
          } catch (e) { /* ignore — token ผิดรูปแบบ ปล่อยผ่าน */ }
        }

        // —— x-encrypted (replay protection) ——
        // แนบเฉพาะ request ที่ไป SERVER_URL และยังไม่ได้ตั้ง x-encrypted เอง
        const isApiCall = url.includes(SERVER_URL) || url.startsWith("/api/");
        if (isApiCall && !config.headers?.["x-encrypted"] && _RKEY) {
          try {
            config.headers["x-encrypted"] = encryptData({ timestamp: Date.now() });
          } catch (e) { /* encrypt ไม่สำเร็จ → ปล่อยผ่าน ไม่บล็อก request */ }
        }
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  axios.interceptors.response.use(
    // ✅ Success — decrypt ຖ້າ response ຖືກ encrypt ໄວ້
    (response) => {
      if (response?.data?.d) response.data = _decryptPayload(response.data);
      return response;
    },

    // ❌ Error — ຈັດການ 401 + network errors
    async (error) => {
      const originalRequest = error.config;
      const status = error?.response?.status;
      const errCode = error?.response?.data?.message || error?.response?.data?.error;

      // ===== 401 Unauthorized: Token ໝົດອາຍຸ ຫຼື ບໍ່ຖືກຕ້ອງ =====
      if (status === 401 && !originalRequest._retry) {
        // ບໍ່ retry ຖ້າເປັນ auth endpoints ຫຼື refresh-token endpoint ເອງ
        const url = originalRequest?.url || "";
        const isAuthEndpoint = url.includes("/login") || url.includes("/register") || url.includes("/forget-password");
        const isRefreshEndpoint = url.includes("/refresh-token");
        
        if (isAuthEndpoint || isRefreshEndpoint) {
          return Promise.reject(error);
        }

        // ★ Token expired — ລອງ refresh ອັດຕະໂນມັດ
        const tokenErrors = ["TOKEN_EXPIRED", "INVALID_TOKEN", "AUTHORIZATION_REQUIRED"];
        if (tokenErrors.includes(errCode) || !errCode) {
          const refreshToken = (() => {
            try {
              const raw = localStorage.getItem("REFRESH_TOKEN_WEB");
              if (raw && raw !== "undefined" && raw !== "null") return JSON.parse(raw);
            } catch (e) { /* ignore */ }
            return null;
          })();

          // ★ ບໍ່ມີ refreshToken → logout ທັນທີ (ພຶດຕິກຳເກົ່າ)
          if (!refreshToken) {
            forceLogout();
            return Promise.reject(error);
          }

          // ★ ຖ້າກຳລັງ refresh ຢູ່ → ເຂົ້າ queue ລໍຖ້າ
          if (isRefreshing) {
            return new Promise((resolve, reject) => {
              refreshQueue.push({ resolve, reject });
            }).then((newToken) => {
              originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
              originalRequest._retry = true;
              return axios(originalRequest);
            }).catch(() => {
              return Promise.reject(error);
            });
          }

          // ★ ເລີ່ມ refresh
          isRefreshing = true;
          originalRequest._retry = true;

          try {
            const res = await axios.post(
              `${SERVER_URL}/api/v1/refresh-token`,
              { refreshToken }
            );
            const newAccessToken = res.data.accessToken;

            // ★ ເກັບ token ໃໝ່
            localStorage.setItem("ACCESS_TOKEN_WEB", JSON.stringify(newAccessToken));

            // ★ ປ່ອຍ queue — ທຸກ request ທີ່ລໍຖ້າ ຈະ retry ດ້ວຍ token ໃໝ່
            processQueue(null, newAccessToken);

            // ★ Retry request ເດີມ
            originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
            return axios(originalRequest);
          } catch (refreshError) {
            // ★ Refresh ລົ້ມ → ລ້າງທັງໝົດ → redirect login (ພຶດຕິກຳເກົ່າ)
            processQueue(refreshError, null);
            forceLogout();
            return Promise.reject(refreshError);
          } finally {
            isRefreshing = false;
          }
        }
      }

      // ===== Network Error: Server ບໍ່ຕອບ =====
      if (!error.response && error.message === "Network Error") {
        console.warn("Network Error - server unreachable");
        // ບໍ່ show popup ທຸກຄັ້ງ — ໃຫ້ component handle ເອງ
      }

      return Promise.reject(error);
    }
  );
}
